import { defineModule } from 'direct-vuex'
import { moduleActionContext, moduleGetterContext } from '../'
import api, { RouteIds } from '@/api'
import { uniqBy, cloneDeep, omit } from 'lodash-es'

import * as ui from '@/types/frontend'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import { processAndSortProjects } from '@/utilities/project-helpers'
import { format } from '@/utilities/audience-helpers.js'
import {
  WorkspaceWithTestsQuery,
  MgtTestQuery
} from '@/api/graphql-sdk.generated'
import analytics from '@/analytics'
import { ProjectCardData } from '@alpha/ink'
import { dbToGraphTestStatus } from '@/utilities/test-helpers'
import { updatePendoMetaData, PendoMetaData } from '@/analytics/pendo'
import { DefaultBranding } from '@/components/Survey/Utilities/StaticColors'

type QueryMgtTest = NonNullable<MgtTestQuery['mgtTest']>
export type QueryWorkspace = NonNullable<WorkspaceWithTestsQuery['workspace']>
export type LegacyWorkspaceTap = NonNullable<QueryWorkspace['mgtWorkspace']>
type QueryWorkspaceAccountAudiences = QueryWorkspace['accountAudiences']
export type ColorBranding = Pick<graph.Branding, 'monochromatic' | 'sequentialPalette' | 'divergentPalette' | 'qualitativePalette'>

export interface WorkspaceState {
  mgtTestsById: Record<string, QueryMgtTest>
  workspace: LegacyWorkspaceTap
  workspaceWithTests: QueryWorkspace
  accountId: string | null
  organizationId: string | null
  accountAudiences: QueryWorkspaceAccountAudiences
  accountAudiencesMap: Record<string, number>
  projectMap: db.Projects
  sharedReports: graph.SharedReports[]
  branding: ColorBranding
}

const createInitialState = (): WorkspaceState => ({
  mgtTestsById: {},
  workspace: {} as LegacyWorkspaceTap,
  workspaceWithTests: {} as QueryWorkspace,
  accountId: null,
  organizationId: null,
  accountAudiences: [],
  accountAudiencesMap: {},
  projectMap: {} as db.Projects,
  sharedReports: [],
  branding: DefaultBranding()
})

const workspace = defineModule({
  namespaced: true,
  state: createInitialState,
  getters: {
    tests (state): db.Test[] {
      if (!state.workspace.drops) return []
      const visibleTests = state.workspace.drops!.filter(test => {
        return !test.payload.hidden
      })
      return uniqBy(visibleTests, (test) => test.id)
    },
    projects (...context): ProjectCardData[] {
      const { state, getters } = workspaceGetterContext(context)
      const projects = processAndSortProjects(state.projectMap)
      return projects.map(project => {
        const { id, ids, title, desc } = project
        const tests: db.Test[] = getters.tests.filter(test => ids?.includes(test.id.toString()))
        return tests.reduce((acc, test) => {
          const { status, segment, tags } = test.payload
          const audience = format.normalizeAudience(segment)
          if (!acc.audiences[audience.id]) {
            acc.audiences[audience.id] = audience
          }
          acc.tags = acc.tags.concat((tags || '').split(',').filter(tag => !!tag))
          acc.testStatusCount[status] = acc.testStatusCount[status] || 0
          acc.testStatusCount[status]++
          return acc
        }, {
          tests: tests,
          testCount: tests.length,
          testStatusCount: {} as Record<db.TestStatus, number>,
          audiences: {},
          reports: [],
          tags: [],
          lastModified: state.workspace.updatedAt, // TODO:COLLIN, we will have to start adding updatedAt timestamps to projects
          ids: ids,
          id: id,
          description: desc,
          title
        } as ProjectCardData)
      }) as ProjectCardData[]
    },
    tags (...context): string[] {
      const { state } = workspaceGetterContext(context)
      const tags = new Set<string>()
      for (const test of state.workspaceWithTests.tests) {
        for (const tag of test.tags) {
          tags.add(tag)
        }
      }
      return Array.from(tags).sort()
    },
    sharedAssets (): Record<string, db.Prototype> {
      return {}
    },
    reports (state, getters): db.Report[] {
      return []
    },
    testCards (...context): ui.TestCard[] {
      const { getters } = workspaceGetterContext(context)
      return getters.tests.map((test) => {
        const id = test.id
        const createdAt = test.createdAt
        const testInfo = <any>test.payload

        const { title, segment, status, testType, people, completedAt } = testInfo

        // TODO: use format.normalizeAudience here and adjust downstream
        const audience = typeof segment === 'string' || segment === undefined ? segment : segment.title

        return {
          id: id,
          status: status,
          types: testType,
          people: people,
          audiences: [audience],
          title,
          createdAt: createdAt,
          completedAt: completedAt,
          lastModified: test.updatedAt
        }
      })
    },
    getProjectById: (...args) => (id: string): ProjectCardData | undefined => {
      const { getters } = workspaceGetterContext(args)
      return getters.projects.find(project => project.id === id)
    },
    getTestById: (state) => (id: string) => {
      return state.mgtTestsById[id].data as db.Test
    },
    getMgtTestById: (state) => (id: string) => {
      return state.mgtTestsById[id]
    },
    getTestsByIds: (...args) => (testIds: string[] | undefined): db.Test[] => {
      const { getters } = workspaceGetterContext(args)
      if (!testIds) { return [] }
      const tests: db.Test[] = getters.tests.filter(test => {
        const testId = test.id.toString()
        return testIds?.includes(testId)
      })

      return tests
    },
    getProjectsByTestId: (state, getters) => (id: string): db.Project[] => {
      const projects = Object.values(state.projectMap).filter(project => {
        return project.ids?.includes(id)
      })
      return projects
    },
    getAudienceById: (state, getters) => (id: string): QueryWorkspaceAccountAudiences[number] | undefined => {
      return state.accountAudiences[state.accountAudiencesMap[id]]
    },
    sharedReportsProjects: (state) => {
      return state.sharedReports.reduce((acc, curr) => {
        if (curr.source_type !== 'experiment') return acc
        if (acc[curr.source_id!]) {
          acc[curr.source_id!].push(curr)
        } else {
          acc[curr.source_id!] = [curr]
        }

        return acc
      }, {} as Record<string, graph.SharedReports[]>)
    },
    sharedReportsTests (state) {
      return state.sharedReports.reduce((acc, curr) => {
        if (curr.source_type !== 'test') return acc
        if (acc[curr.source_id!]) {
          acc[curr.source_id!].push(curr)
        } else {
          acc[curr.source_id!] = [curr]
        }

        return acc
      }, {} as Record<string, graph.SharedReports[]>)
    },
    projectMap (state) {
      return state.workspace.payload.reports
    },
    queue (state) {
      return state.workspace.payload?.priority
    },
    accountAudiences (state) {
      return state.accountAudiences
    },
    workspaceAudiences (state) {
      return state.accountAudiences.filter(a => a.payload.organization_id === state.organizationId)
    }
  },
  mutations: {
    updateTest (state, updatedTest: db.Test) {
      const testIndex = state.workspace.drops?.findIndex(test => test.id === updatedTest.id)
      if (!testIndex) {
        console.warn('Updated a test that cant be found in the store')
        state.workspace.drops!.unshift(updatedTest)
      } else {
        state.workspace.drops!.splice(testIndex, 1, updatedTest)
      }

      state.mgtTestsById = {
        ...state.mgtTestsById,
        [updatedTest.id]: {
          ...state.mgtTestsById[updatedTest.id],
          data: updatedTest
        }
      }
      document.title = `${updatedTest.payload.title} - Feedback Loop`
    },
    removeTest (state, testId: number) {
      const testIndex = state.workspace.drops?.findIndex(test => test.id === testId)
      if (!testIndex) {
        console.warn('Deleting a test that cant be found in the store')
      } else {
        state.workspace.drops!.splice(testIndex, 1)
      }

      state.mgtTestsById = omit(state.mgtTestsById, testId.toString())
    },
    changeGraphqlWorkspace (state, workspace: QueryWorkspace) {
      state.workspace = workspace.mgtWorkspace
      state.projectMap = workspace.mgtWorkspace.payload.reports || {}
      state.workspaceWithTests = workspace
      state.accountId = workspace.accountId
      state.organizationId = workspace.organizationId
      state.accountAudiences = workspace.accountAudiences
      state.accountAudiencesMap = state.accountAudiences.reduce((accum, curr, idx) => {
        accum[curr._id] = idx
        return accum
      }, {} as Record<string, number>)
    },
    setBranding (state, branding: ColorBranding) {
      state.branding = Object.assign({}, branding)
    },
    reset (state) {
      Object.assign(state, createInitialState())
    },
    addProject (state, project: db.Project) {
      // https://vuex.vuejs.org/guide/mutations.html#mutations-follow-vue-s-reactivity-rules
      state.projectMap = { ...state.projectMap, [project.id!]: project }
    },
    editProject (state, project: Partial<db.Project>) {
      const { id, ...rest } = project
      if (!id) {
        console.error('Failed to update project in state, missing id')
      } else {
        const newProject = Object.assign(state.projectMap[id], rest)
        state.projectMap[id] = newProject
      }
    },
    deleteProject (state, projectId) {
      const newProjectMap = cloneDeep(state.projectMap)
      delete newProjectMap[projectId]
      state.projectMap = newProjectMap
    },
    setSharedReports (state, sharedReports: graph.SharedReports[]) {
      state.sharedReports = sharedReports
    },
    setAccountAudiences (state, accountAudiences: QueryWorkspaceAccountAudiences) {
      state.accountAudiences = accountAudiences
    },
    addMgtTest (state, test: QueryMgtTest) {
      state.mgtTestsById = { ...state.mgtTestsById, [test.data.id]: test }
    },
    addAccountAudience (state, audience: graph.Audience) {
      state.accountAudiences = [...state.accountAudiences, audience]
      state.accountAudiencesMap[audience._id] = state.accountAudiences.length - 1
    }
  },
  actions: {
    reset (context) {
      const { commit } = workspaceActionContext(context)
      commit.reset()
    },
    async setWorkspace (context, workspace: QueryWorkspace): Promise<void> {
      const { commit } = workspaceActionContext(context)
      commit.changeGraphqlWorkspace(workspace)
      if (workspace.branding) commit.setBranding(workspace.branding)
      analytics.group(workspace.accountId)
      const newPendoMetaData: PendoMetaData = {
        account: {
          id: workspace.accountId,
          workspaceType: workspace.mgtWorkspace.payload.type
        }
      }
      updatePendoMetaData(newPendoMetaData)
    },
    async updateTest (context, payload: { test: Partial<db.TestPayload>, workspaceId: string, testId: string }) {
      const { commit } = workspaceActionContext(context)

      const { test, workspaceId, testId } = payload
      const { status, ...rest } = test

      const newPayload : graph.UpdateTestInput = {
        status: status ? dbToGraphTestStatus(status) : undefined,
        ...rest
      }

      try {
        const res = await api.updateTest(workspaceId, testId, newPayload)
        if (res.id) {
          const test = await api.getMgtTest(payload.workspaceId, payload.testId)
          if (test) {
            commit.updateTest(test.data)
          }
        }
        return res
      } catch (error) {
        console.error(error)
      }
    },
    async deleteTest (context, payload: Pick<RouteIds, 'workspaceId' | 'testId'>): Promise<boolean | undefined> {
      const { commit } = workspaceActionContext(context)
      if (!payload.testId) {
        console.error('Tried to delete test without passing an id')
      } else {
        try {
          await api.deleteTest(payload)
          commit.removeTest(parseInt(payload.testId))
          return true
        } catch (error) {
          console.error(error)
        }
      }
    },
    async deleteAssets (_, payload: { workspaceId: string, testId: string, assetKeys: string[] }) {
      await api.deleteAssets(payload.workspaceId, payload.testId, payload.assetKeys)
    },
    async createProject (context, payload: { workspaceId: string, title: string, description?: string }): Promise<db.Project> {
      const project = await api.createProject(payload.workspaceId, payload.title, payload.description)
      const { commit } = workspaceActionContext(context)
      const fullProject = {
        audiences: [] as string[],
        assets: {} as db.Project['assets'],
        type: 'dynamic',
        ...project
      }
      commit.addProject(fullProject)
      analytics.track('Project Added', { projectId: project.id })
      return fullProject
    },
    async addExistingTestToProject (context, payload: { projectId: string, testId: number}): Promise<db.Project | undefined> {
      const { dispatch, getters } = workspaceActionContext(context)
      const { projectId, testId } = payload
      const project = getters.getProjectById(projectId)
      if (!project) {
        console.error(`Cannot find project for projectId: ${projectId}`)
        return
      }
      let ids
      if (project.ids) {
        ids = project.ids.concat([testId.toString()])
      } else {
        ids = [testId.toString()]
      }
      return dispatch.updateProject({
        id: projectId,
        projectData: {
          ids
        }
      })
    },
    async updateBranding (context, payload: ColorBranding): Promise<graph.Branding | undefined> {
      const { state, commit } = workspaceActionContext(context)
      const workspaceId = state.workspace.id
      const results = await api.updateBranding(workspaceId, payload)
      if (results) commit.setBranding(results)
      return results
    },
    async updateProject (context, projectToEdit: { id: string, projectData: graph.UpdateProjectInput }): Promise<db.Project | undefined> {
      const { state, commit } = workspaceActionContext(context)
      const { id, projectData } = projectToEdit

      const newProjectData: Partial<db.Project> = {}
      if (projectData.assets) newProjectData.assets = projectData.assets
      if (projectData.title) newProjectData.title = projectData.title
      if (projectData.audiences) newProjectData.audiences = projectData.audiences
      if (projectData.ids) newProjectData.ids = projectData.ids
      if (projectData.desc) newProjectData.desc = projectData.desc
      if (projectData.hidden) newProjectData.hidden = projectData.hidden

      if (!id) {
        console.error('Project edits must include a project id')
        return
      }

      const existingProject = state.projectMap[id]
      const newProject = Object.assign(existingProject, newProjectData)

      await api.updateProject(state.workspace.id, id, projectData)
      analytics.track('Project Updated', { projectId: id })
      commit.editProject(newProject)

      return newProject
    },
    async deleteProject (context, payload: { workspaceId: string, projectId: string, projectInput: graph.UpdateProjectInput }): Promise<db.Project | undefined> {
      const { state } = workspaceActionContext(context)
      const newProject = Object.assign(state.projectMap[payload.projectId], payload.projectInput)
      await api.updateProject(payload.workspaceId, payload.projectId, payload.projectInput)
      analytics.track('Project Deleted', { projectId: payload.projectId })
      return newProject
    },
    async loadTest (context, payload: { workspaceId: string, testId: string }): Promise<QueryMgtTest | undefined> {
      const { commit, state } = workspaceActionContext(context)
      const existingTest = state.mgtTestsById[payload.testId] // TODO:COLLIN might want to rework this if we ever cache vuex store data between reloads
      if (state.mgtTestsById[payload.testId]) return existingTest
      const test = await api.getMgtTest(payload.workspaceId, payload.testId)
      if (test) {
        commit.addMgtTest(test)
      }

      return test
    },
    updateAccountAudiences (context, payload) {
      const { commit } = workspaceActionContext(context)
      commit.setAccountAudiences(payload)
    }
  }
})

export default workspace
export const workspaceGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, workspace)
export const workspaceActionContext = (context:any) => moduleActionContext(context, workspace)
